/* eslint-disable max-lines */
import {makeAutoObservable, observable} from 'mobx'
import {Lang} from 'yougile-lang'
import {ArrUtil, ObjUtil, Option, Util} from 'yougile-util'
import {
  DataObjectId,
  getNextScheduledReadable,
  getStickerType,
  Hub,
  noBoardContainerId,
  RepeatStickerEntry,
  repeatTemplatesContainerId,
  SandboxTree,
  scheduleToReadable,
  Tag,
  taskTemplatesContainerId,
  User
} from 'yougile-model'
import {Items} from '@api/model/items'
import {Accounts} from '@client/accounts'
import {CompanyPreferencesClient} from '@client/company-preferences-client'
import {DepartmentsClient} from '@client/departments-client'
import {PermissionsChecker} from '@client/permissions-checker'
import {Storage} from '@client/storage'
import {Sync} from '@client/sync'
import {SyncEvents} from '@client/sync-events'
import {Tracking, TrackingEvent} from '@common/tracking'
import {showDeleteConfirm} from '@components/common/base/notifications'
import {MessageBox} from '@components/message-box'
import {getAllTasksFromPrivateLists} from '@components/my-tasks/utils'
import {createUseStore} from '@containers/hooks'
import {ChatUtils} from '@extensions/chat-utils'
import {Column} from '@extensions/column'
import {Gantt} from '@extensions/gantt'
import {globals} from '@extensions/globals-base'
import {PageService} from '@extensions/page-service'
import {
  Sticker,
  stickerCtl,
  systemStickerCtl
} from '@extensions/sticker/sticker'
import {SubtaskUtil} from '@extensions/subtask-util'
import {excludeTaskTemplateStickers, Task} from '@extensions/task'
import {UserAccess} from '@extensions/user-access'
import {canAssignUser} from '../../../extensions/sticker/views-web/user-view'
import {
  AssignedType,
  ChooseLocationType,
  RepeatType,
  ResponsibleType,
  SpoilersType,
  State,
  StickerListType
} from './types'

const STORAGE_SPOILERS = 'spoilers'

const spoilersInit: SpoilersType = {
  info: true,
  availabilityInfo: false,
  connectedTasks: false,
  dependentTasks: false,
  dependsOnTasks: false,
  log: true
}

const responsibleInit: ResponsibleType = {users: [], onChoose: Util.noop}
const assignedInit: AssignedType = {...responsibleInit, available: []}
const stickerListInit: StickerListType = {
  allInBoard: () => [],
  menuOpen: false,
  add: Util.noop,
  onUnpin: Util.noop,
  openStates: false
}
const repeatInit: RepeatType = {
  getLocationTitle: () => ['', '', ''],
  goToBoard: Util.noop,
  isStopped: () => false,
  getNearestRepeat: () => '',
  getConditions: () => ''
}
const chooseLocationInit: ChooseLocationType = {open: false, onDone: Util.noop}

export class TaskInfoLogStore {
  state: State = {
    spoilers: spoilersInit,
    noBoard: false,
    isPrivate: false,
    isFromPrivateList: false,
    locationTitle: '',
    stickerTypes: new Set(),
    stickers: [],
    responsible: responsibleInit,
    assigned: assignedInit,
    stickerList: stickerListInit,
    splitLocationTitle: [],
    hiddenLocation: false,
    repeat: repeatInit,
    chooseLocation: chooseLocationInit,
    isRepeatTemplate: false
  }

  stickerOrderIx: {[key: string]: number} = {}
  boardListenerId = 0
  projectListenerId = 0

  constructor() {
    makeAutoObservable(
      this,
      {
        state: observable.deep
      },
      {
        autoBind: true
      }
    )
  }

  spoilersToggleInfo(): void {
    this.toggleLogic('info')
  }

  spoilersToggleAvailabilityInfo(): void {
    this.toggleLogic('availabilityInfo')
  }

  spoilersToggleConnectedTasks(): void {
    this.toggleLogic('connectedTasks')
  }

  spoilersToggleDependsOnTasks(): void {
    this.toggleLogic('dependsOnTasks')
  }

  spoilersToggleDependentTasks(): void {
    this.toggleLogic('dependentTasks')
  }

  spoilersToggleLog(): void {
    this.toggleLogic('log')
  }

  spoilersSave(): void {
    Storage.setItem(STORAGE_SPOILERS, JSON.stringify(this.state.spoilers))
  }

  private loadSpoilers(): void {
    const item = Storage.getItemAsJson<SpoilersType>(STORAGE_SPOILERS)
    if (item) {
      this.state.spoilers = item
    }
  }

  private toggleLogic(item: keyof State['spoilers']): void {
    this.state.spoilers = {
      ...this.state.spoilers,
      [item]: !this.state.spoilers[item]
    }
    this.spoilersSave()
  }

  private getBox(): SandboxTree {
    const hub = this.state.hub
    if (Task.isTaskTemplate(hub)) {
      return Sync.getCurrentSandbox()
    }
    return hub ? Sync.get(hub) : Sync.getCurrentSandbox()
  }

  // eslint-disable-next-line max-lines-per-function
  syncOnNew(hub: Hub): void {
    this.cleanup()

    this.state.hub = hub
    this.state.spoilers = {...spoilersInit}
    this.loadSpoilers()

    if (!hub) {
      return
    }

    this.state.locationTitle = ChatUtils.getChatLocation(hub)
    this.state.noBoard = hub.location === noBoardContainerId

    const {state} = this

    this.state.chooseLocation = {
      open: false,
      onDone(chosen) {
        state.chooseLocation.open = false
        const column = Sync.getCurrentSandbox().getHub(chosen)
        if (column) {
          Column.taskDropHandler(0, hub, column, {
            ignoreBoardFilters: true,
            type: 'menu'
          })
        }
      }
    }

    const update = this.update
    this.state.stickerList = {
      menuOpen: false,
      stickerWithOpenMenu: undefined,
      openStates: false,
      allInBoard() {
        if (!state.hub) {
          return []
        }
        const box = Sync.get(state.hub)
        const parentIds = Task.getAllColumnLevelParentTaskIds(box, state.hub.id)

        const hub = state.hub

        if (Task.isTaskTemplate(hub) && hub.data.taskTemplate) {
          const board = box.getHub(hub.data.taskTemplate.boardId)
          return Sticker.list(board).filter(
            s => !excludeTaskTemplateStickers.includes(s.title)
          )
        }

        if (hub.location === noBoardContainerId && !parentIds.length) {
          // case 1: task is not on board and has no parents
          return Sticker.list(box).filter(s => {
            const type = getStickerType(s)
            return (
              type === 'sprint' ||
              type === 'string' ||
              type === 'deadline' ||
              type === 'timer' ||
              type === 'stopwatch'
            )
          })
        } else {
          // case 2, 3: task is either on board, or has parents and is not assigned to a board
          let stickers: Tag[] = []
          const columnTaskIds = parentIds.length ? parentIds : [hub.id]
          for (const taskId of columnTaskIds) {
            const hub = box.getHub(taskId)
            if (!hub) {
              continue
            }

            const repeatCol = state.isRepeatTemplate
              ? box.getHub(hub.data?.repeatTemplate?.columnId)
              : undefined
            const col = repeatCol ?? box.getHub(hub.location)
            const board = box.getHub(col?.location)
            const boardStickers = Sticker.list(board).filter(sticker => {
              const type = getStickerType(sticker)
              return type !== 'user' && (!repeatCol || type !== 'repeat')
            })
            stickers = stickers.concat(boardStickers)
          }
          return ArrUtil.unique(stickers, x => x.id)
        }
      },
      add(sticker: Tag) {
        const type = getStickerType(sticker)
        state.stickerList.menuOpen = false
        if (type === 'user') {
          const el: HTMLElement | null = document.querySelector(
            '.sticker-item.sticker-item-user'
          )
          el?.click()
        } else {
          if (
            ['sprint', 'string', 'text', 'numeric', 'deadline'].includes(type)
          ) {
            this.edited = sticker?.id
            state.stickerList.stickerWithOpenMenu = sticker?.id
          }
          if (state.hub) {
            Sticker.pin(sticker, 'chat', state.hub, undefined)
          }
          update()
        }
      },

      onUnpin() {
        this.edited = undefined
        update()
      }
    }

    this.state.responsible = {
      users: [],
      onChoose(chosen: DataObjectId[]) {
        if (!hub || !chosen || !chosen[0]) {
          return
        }
        if (
          !Task.isTaskTemplate(hub) &&
          !PermissionsChecker.checkAssignUser(hub, chosen[0])
        ) {
          MessageBox.warn(Lang.msg.permissionsForbiddenAssignUser)
          return
        }
        const responsibleId = chosen[0]
        const assigned = Sticker.getUsers(hub).filter(
          id => id !== responsibleId
        )
        Sticker.setUsers(hub, [responsibleId, ...assigned], 'chat')
      }
    }

    this.state.assigned = {
      users: [],
      available: [],
      onChoose: this.onChooseAssigned
    }

    const box = this.getBox()

    this.boardListenerId = SyncEvents.addListener(
      box.companyId,
      () => box.get(hub.location)?.location,
      () => this.update()
    )

    const projectId = this.projectId()
    if (projectId) {
      this.projectListenerId = SyncEvents.addListener(
        box.companyId,
        projectId,
        () => this.update()
      )
    }

    this.syncOnUpdate()
  }

  syncOnUpdate(): void {
    this.onLocationUpdate()
    this.update()
  }

  repeatUpdate(): void {
    if (!this.state.isRepeatTemplate) {
      return
    }

    const {state} = this
    this.state.repeat = {
      getLocationTitle() {
        const box = Sync.getOrCurrent(state.hub)
        const tpl = state.hub?.data.repeatTemplate
        const colId = tpl?.columnId
        const col = box.getHub(colId)
        const board = box.getHub(col?.location)
        const proj = box.getHub(board?.location)
        return [proj?.title ?? '', board?.title ?? '', col?.title ?? ''] as [
          string,
          string,
          string
        ]
      },
      goToBoard() {
        const box = Sync.getOrCurrent(state.hub)
        const tpl = state.hub?.data.repeatTemplate
        const colId = tpl?.columnId
        const col = box.getHub(colId)
        col && PageService.goToBoard(col.location, box.companyId)
      },
      isStopped() {
        return !state.hub?.data.repeatTemplate?.schedule
      },
      getNearestRepeat() {
        const tpl = state.hub?.data.repeatTemplate
        return getNextScheduledReadable(tpl?.schedule)
      },
      getConditions() {
        const tpl = state.hub?.data.repeatTemplate
        return scheduleToReadable(tpl?.schedule)
      }
    }
  }

  projectId(): DataObjectId | undefined {
    const hub = this.state.hub
    return hub ? Task.parentProject(hub)?.id : undefined
  }

  onLocationUpdate(): void {
    if (!this.state.hub) {
      this.state.locationTitle = ''
      this.state.splitLocationTitle = []
      this.state.hiddenLocation = false
    } else {
      this.state.locationTitle = ChatUtils.getChatLocation(this.state.hub)
      this.state.splitLocationTitle = ChatUtils.getSplitChatLocation(
        this.state.hub
      )
      this.state.noBoard = this.state.hub.location === noBoardContainerId
      this.state.hiddenLocation =
        !this.state.noBoard && !this.state.splitLocationTitle.length
    }
  }

  cleanup(): void {
    if (this.boardListenerId) {
      SyncEvents.removeListener(this.boardListenerId)
      this.boardListenerId = 0
    }
    if (this.projectListenerId) {
      SyncEvents.removeListener(this.projectListenerId)
      this.projectListenerId = 0
    }
  }

  // eslint-disable-next-line max-lines-per-function
  update(): void {
    if (!this.state.hub) {
      return
    }
    this.state.stickers = Sticker.getAllPinned(this.state.hub) || []
    this.state.stickerTypes = new Set()
    this.state.isPrivate = !!this.state.hub.data.private

    const parents = this.parentTasks()
    const allTasksFromPrivateLists = getAllTasksFromPrivateLists()

    this.state.isFromPrivateList = !parents.length
      ? allTasksFromPrivateLists.includes(this.state.hub.id)
      : parents.some(parent => allTasksFromPrivateLists.includes(parent.id))

    this.state.isRepeatTemplate =
      this.state.hub.location === repeatTemplatesContainerId
    this.repeatUpdate()

    const assigned = Sticker.getUsers(this.state.hub)
    this.state.responsible.users = assigned.slice(0, 1)
    this.state.assigned.users = assigned

    if (this.state.hub.location === taskTemplatesContainerId) {
      const box = Sync.getCurrentSandbox()
      const projectId = box.getHub(this.state.hub.data.taskTemplate?.boardId)
        ?.location
      const project = box.getHub(projectId)
      const users = UserAccess.listUsersInProject(project)

      this.state.assigned.available = users.map(u => u.id)
    } else {
      const box = this.getBox()
      const repeatCol = this.state.isRepeatTemplate
        ? box.getHub(this.state.hub?.data?.repeatTemplate?.columnId)
        : undefined

      const calculatedHierarchy = DepartmentsClient.getHierarchy(box)
      const taskForPermissions = repeatCol ?? this.state.hub

      const allUsers = box.getAllUsers(false).map(u => u.id)

      this.state.assigned.available = allUsers.filter(uid =>
        canAssignUser(taskForPermissions, uid, calculatedHierarchy)
      )
    }

    let hasEdited = false

    this.state.stickers.sort((a, b) => {
      const atype = Sticker.getType(a)
      const btype = Sticker.getType(b)
      return (
        (this.stickerOrderIx[atype] || -1) - (this.stickerOrderIx[btype] || -1)
      )
    })
    let hasUser = false
    this.state.stickers.forEach(s => {
      const type = Sticker.getType(s)
      this.state.stickerTypes.add(type)
      if (s.id === this.state.stickerList.edited) {
        hasEdited = true
      }
      if (type === 'user') {
        hasUser = true
      }
    })
    if (this.state.stickerList.edited) {
      if (!hasEdited) {
        const editedSticker = this.getBox().get(
          this.state.stickerList.edited
        ) as Tag
        this.state.stickers = [...this.state.stickers, editedSticker]
      } else {
        this.state.stickerList.edited = undefined
      }
    }
    if (!hasUser) {
      const userTag = stickerCtl.User.getSticker(this.getBox())
      if (this.state.stickerList.edited !== userTag.id) {
        this.state.stickers = [userTag, ...this.state.stickers]
      } else {
        this.state.stickerTypes.add('user')
      }
    }
  }

  makePublic(): void {
    if (!this.state.hub) {
      return
    }
    delete this.state.hub.data.private
    this.state.hub.commit()
  }

  allowConnect(): boolean {
    return Boolean(
      this.state.hub && PermissionsChecker.checkConnectSingle(this.state.hub)
    )
  }

  canEditStickers(): boolean {
    return (
      !!this.state.hub &&
      PermissionsChecker.checkEditTaskStickers(
        this.state.hub,
        'not-user',
        undefined
      )
    )
  }

  parentTasks(): Hub[] {
    if (!this.state.hub) {
      return []
    }
    const box = Sync.getOrCurrent(this.state.hub)
    return SubtaskUtil.getParentTaskIds(this.state.hub?.id)
      .map(id => box.getHub(id))
      .filter(Util.isDefined)
      .filter(h => !box.isDeleted(h.id))
  }

  creator(): User | undefined {
    return Task.creator(this.state.hub)
  }

  assignedBy(): DataObjectId[] {
    return this.state.hub ? Task.getAssignedBy(this.state.hub) : []
  }

  goToBoard(): void {
    if (!this.state.hub) {
      return
    }
    const board = Task.parentBoard(this.state.hub)
    if (board) {
      PageService.goToBoard(board.id, globals.getCompanyId())
    }
  }

  openChat(hub: Hub): void {
    if (!hub || !Items.click(Items.fromHub(hub))) {
      return
    }
    ChatUtils.openChat(hub)
  }

  stickerListMenuOpen(menuOpen: boolean): void {
    this.state.stickerList.menuOpen = menuOpen
  }

  chooseLocationOpen(open: boolean): void {
    this.state.chooseLocation.open = open
  }

  hasDeadline(): boolean {
    return !!this.state.stickers?.some(t => t.title === 'Deadline')
  }

  async disconnectTask(
    task: Hub,
    type: 'connection' | 'dependency' | 'dependent'
  ): Promise<void> {
    if (!this.state.hub) {
      return
    }
    if (!PermissionsChecker.checkConnect(task, this.state.hub)) {
      MessageBox.warn(Lang.msg.permissionsForbiddenTaskConnect)
    } else {
      if (await showDeleteConfirm(Lang.msg.confirmDisconnect)) {
        if (type === 'dependency') {
          this.state.hub.data.dependencies &&
            delete this.state.hub.data.dependencies[task.id]
          this.state.hub.commit()

          Gantt.unlinkTasks(task, this.state.hub)

          void Tracking.send(TrackingEvent.ganttDeleteDependencyViaRightPanel)
        } else if (type === 'dependent') {
          task.data.dependencies &&
            delete task.data.dependencies[this.state.hub.id]
          task.commit()

          Gantt.unlinkTasks(this.state.hub, task)

          void Tracking.send(TrackingEvent.ganttDeleteDependencyViaRightPanel)
        } else {
          Hub.disconnect(task, this.state.hub)
        }
      }
    }
  }

  listConnectedHubs(): Hub[] {
    if (!this.state.hub) {
      return []
    }
    const box = Sync.getOrCurrent(this.state.hub)
    const hierarchy = DepartmentsClient.getHierarchy(box)
    return this.state.hub
      .listConnectedHubs()
      .map(hid => box.get(hid) as Hub)
      .filter(
        h => h && !h.deleted && !box.getAllParentHubs(h).find(p => p.deleted)
      )
      .filter(t => ChatUtils.isAvailableChat(box, t.id, hierarchy))
  }

  listRepeatedTasks(): Hub[] {
    const box = Sync.getOrCurrent(this.state.hub)
    const sticker = systemStickerCtl.Repeat.getSticker(box)
    const task = this.state.hub
    if (!sticker || !task) {
      return []
    }
    const val = task.stickers[sticker.id] as RepeatStickerEntry
    if (!val) {
      return []
    }
    const repeatTemplate = Option.from(val)
      .map(v => v.templateId)
      .map(id => box.get(id) as Hub)
      .map(h => h.data.repeatTemplate)
      .get()

    if (repeatTemplate?.allTasks && repeatTemplate.allTasks.length > 1) {
      return repeatTemplate.allTasks
        .map(id => box.get(id) as Hub)
        .filter(h => h && h.id !== task.id)
    } else {
      return []
    }
  }

  listDependent(): ReadonlyArray<Hub> {
    if (!this.state.hub) {
      return []
    }

    const {useNewGantt} = CompanyPreferencesClient.get(globals.getCompanyId())
    if (useNewGantt) {
      return Gantt.getLinksToHubs(this.state.hub)
    }

    return ChatUtils.getDependentTasks(this.getBox(), this.state.hub)
  }

  listDependencies(): ReadonlyArray<Hub> {
    if (!this.state.hub) {
      return []
    }

    const box = Sync.getOrCurrent(this.state.hub)

    const {useNewGantt} = CompanyPreferencesClient.get(globals.getCompanyId())
    if (useNewGantt) {
      return Gantt.getLinkedFromHubs(this.state.hub)
    }

    const d = this.state.hub.data.dependencies || {}
    const ids = ObjUtil.list(d, id => id as DataObjectId)
    return ids
      .map(id => box.get(id) as Hub)
      .filter(
        h => !!h && !h.deleted && !box.getAllParentHubs(h).find(p => p.deleted)
      )
  }

  private onChooseAssigned = (chosenByUser: DataObjectId[]): void => {
    const hub = this.state.hub
    if (!hub) {
      return
    }

    const previousAssignedUsers = Sticker.getUsers(hub)
    const diff = ArrUtil.diff(previousAssignedUsers, chosenByUser)

    for (const userIdToAdd of diff.add) {
      if (!PermissionsChecker.checkAssignUser(hub, userIdToAdd)) {
        const userName = Accounts.getName(userIdToAdd)
        const message = `${Lang.msg.permissionsForbiddenAssignUser} (${userName})`
        MessageBox.warn(message)
        return
      }
    }

    for (const userIdToRemove of diff.remove) {
      if (userIdToRemove === globals.currentUserId) {
        continue
      }
      if (!PermissionsChecker.checkAssignUser(hub, userIdToRemove)) {
        const userToRemoveName = Accounts.getName(userIdToRemove)
        const message = `${Lang.msg.permissionsForbiddenAssignUser} (cannot unassign ${userToRemoveName})`
        MessageBox.warn(message)
        return
      }
    }

    Sticker.setUsers(hub, chosenByUser, 'chat')
  }
}

export const [TaskInfoLogContext, useTaskInfoLogStore] =
  createUseStore(TaskInfoLogStore)
